/**
 * Dinheiro do campo personalizado do tipo `currency`.
 *
 * A moeda é ESCOLHIDA no campo (`attribute_currency`, #1243) e chega aqui como
 * código ISO 4217. Antes ela era deduzida do idioma da conta, o que é palpite:
 * conta brasileira que cobra em dólar não tinha como dizer isso, e trocar o
 * idioma da interface mudava o dinheiro exibido em toda a base.
 *
 * O LOCALE continua entrando, e faz outra coisa: a moeda decide o SÍMBOLO e
 * quantas casas decimais existem (o iene não tem centavo, o dinar kuwaitiano
 * tem três); o locale decide a GRAFIA do número -- `1.234,50` em pt-BR,
 * `1,234.50` em en -- e a grafia do próprio símbolo, que muda com quem lê
 * (`US$` em pt-BR, `$` em en). É o mesmo critério do resto do sistema: quem lê
 * é quem manda no formato, o dado é que manda na unidade.
 *
 * A validação do locale é a da #587 -- o `Intl` NÃO lança para tag desconhecida
 * mas sintaticamente válida ('not-a-locale' tem essa forma): ele silenciosamente
 * usa o locale do AMBIENTE, que no navegador é o do usuário.
 *
 * Antes disto cada tela inventava o seu: `settings/billing/Index.vue`,
 * `flipLeads/*` e `franchises/*` têm treze `Intl.NumberFormat` com `'pt-BR'` e
 * `'BRL'` cravados. Este arquivo é o lugar para onde elas podem migrar; esta
 * issue não as move para não misturar escopos.
 */

// Locale padrão da aplicação (config/application.rb), usado quando o código
// recebido não é reconhecido pelo Intl.
const FALLBACK_LOCALE = 'pt-BR';

// Espelha `CustomAttributeDefinition::DEFAULT_CURRENCY`. É o que os campos
// criados antes da #1243 passaram a valer no backfill, e o que um campo sem
// moeda escolhida exibe.
export const DEFAULT_CURRENCY = 'BRL';

// Quantas casas decimais assumir quando o `Intl` não souber dizer (ambiente sem
// dados de moeda, código fora do catálogo). Duas é o caso de quase todo o
// catálogo ISO 4217.
export const DEFAULT_CURRENCY_DECIMALS = 2;

// `\A[A-Z]{3}\z` é o mesmo contrato que o modelo valida no servidor.
const CURRENCY_CODE_FORMAT = /^[A-Z]{3}$/;

// Só entra em ação em navegador sem `Intl.supportedValuesOf` (anterior ao
// Chrome 99 / Safari 15.4). Não pretende ser o catálogo ISO 4217 -- é o mínimo
// para o seletor não nascer vazio, e o código já escolhido é sempre
// acrescentado a ele por `getCurrencyOptions`.
const FALLBACK_CURRENCIES = [
  'ARS',
  'AUD',
  'BRL',
  'CAD',
  'CHF',
  'CLP',
  'CNY',
  'COP',
  'EUR',
  'GBP',
  'JPY',
  'MXN',
  'PYG',
  'USD',
  'UYU',
];

/**
 * Resolve o código para uma tag que o Intl reconhece, ou cai no padrão da
 * aplicação. Espelha `resolveDatePickerLocale` de `timeHelper.js`.
 *
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @returns {string} Tag BCP-47 suportada, ou o padrão da aplicação
 */
export const resolveCurrencyLocale = localeCode => {
  const tag = String(localeCode || '').replace('_', '-');
  if (!tag) return FALLBACK_LOCALE;

  try {
    const [supported] = Intl.NumberFormat.supportedLocalesOf([tag]);
    return supported || FALLBACK_LOCALE;
  } catch (error) {
    // Tag sintaticamente inválida: o supportedLocalesOf lança RangeError.
    return FALLBACK_LOCALE;
  }
};

/**
 * Normaliza o código da moeda para a grafia do ISO 4217, caindo no padrão
 * quando o campo não tem escolha gravada (campo antigo, tipo recém-trocado).
 *
 * @param {string} currencyCode
 * @returns {string} Ex.: 'BRL'
 */
export const normalizeCurrencyCode = currencyCode => {
  const codigo = String(currencyCode || '')
    .trim()
    .toUpperCase();

  return CURRENCY_CODE_FORMAT.test(codigo) ? codigo : DEFAULT_CURRENCY;
};

// Moedas que o catálogo do navegador ainda devolve e que NÃO circulam mais.
// Oferecer moeda morta num seletor de cadastro é convidar o cliente a escolher
// errado -- e o dado fica lá, calado, até alguém reparar.
//
// A lista é curta porque o `Intl.supportedValuesOf('currency')` já é o conjunto
// corrente (159 códigos no Chrome medido, e nenhum cruzeiro: a única moeda
// brasileira nele é `BRL`). Estas cinco são as que sobraram, medidas uma a uma
// no navegador:
//
//   CUC  peso cubano conversível, extinto em 2021
//   HRK  kuna croata, substituída pelo euro em 2023
//   SLL  leone antigo, substituído pelo SLE em 2022
//   SVC  colón salvadorenho, fora de circulação desde 2001 (El Salvador usa USD)
//   ZWL  dólar do Zimbábue de 2009, substituído pelo ZWG em 2024
const RETIRED_CURRENCIES = ['CUC', 'HRK', 'SLL', 'SVC', 'ZWL'];

// O outro lado da mesma moeda: as SUBSTITUTAS que o ICU de hoje ainda não tem.
// Sem elas, tirar `SLL` e `ZWL` deixaria Serra Leoa e Zimbábue sem nenhuma
// opção. O `Intl` formata código que não conhece assim mesmo -- ele desenha o
// próprio código no lugar do símbolo (`SLE 1.000,00`), que é o comportamento
// padrão do ISO 4217 e não um erro.
const EXTRA_CURRENCIES = ['SLE', 'ZWG'];

// O ICU marca moeda superada pondo o ano (ou o intervalo) no nome:
// "Leone de Serra Leoa (1964—2022)", "Dólar do Zimbábue (2009)". A regra casa
// pelo DÍGITO, não pela palavra, então vale em qualquer idioma -- é a rede que
// pega a próxima moeda a morrer sem ninguém precisar editar a lista acima.
const SUPERSEDED_NAME = /\(\s*\d{4}/;

/**
 * Os códigos de moeda EM CIRCULAÇÃO que o ambiente conhece.
 *
 * O catálogo sai do próprio `Intl` em vez de uma constante deste repositório:
 * lista de moeda escrita à mão nasce desatualizada e envelhece calada. O que
 * este repositório mantém é só a diferença -- o que tirar e o que acrescentar.
 *
 * @returns {string[]}
 */
const supportedCurrencies = () => {
  let codigos = FALLBACK_CURRENCIES;

  try {
    const doAmbiente = Intl.supportedValuesOf('currency');
    if (Array.isArray(doAmbiente) && doAmbiente.length) codigos = doAmbiente;
  } catch (error) {
    // Ambiente sem `supportedValuesOf`: cai na lista mínima.
  }

  return codigos
    .filter(codigo => !RETIRED_CURRENCIES.includes(codigo))
    .concat(EXTRA_CURRENCIES);
};

/**
 * Nome da moeda no idioma de quem lê ("Dólar americano", "US Dollar").
 *
 * @param {string} currencyCode
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @returns {string} O nome, ou o próprio código quando o ambiente não o tem
 */
export const getCurrencyName = (currencyCode, localeCode) => {
  const codigo = normalizeCurrencyCode(currencyCode);

  try {
    const nome = new Intl.DisplayNames(resolveCurrencyLocale(localeCode), {
      type: 'currency',
    }).of(codigo);

    return nome || codigo;
  } catch (error) {
    return codigo;
  }
};

/**
 * As opções do seletor de moeda, em ordem alfabética pelo nome exibido.
 *
 * `selected` é acrescentado mesmo que o ambiente não o conheça, para que abrir
 * um campo antigo num navegador mais pobre não apague calado a moeda escolhida
 * ao salvar.
 *
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @param {string} [selected] - Código já escolhido no campo
 * @returns {Array<{code: string, name: string, label: string}>}
 */
export const getCurrencyOptions = (localeCode, selected) => {
  const escolhida = selected ? normalizeCurrencyCode(selected) : '';
  const codigos = new Set(supportedCurrencies());
  if (escolhida) codigos.add(escolhida);
  codigos.add(DEFAULT_CURRENCY);

  const idioma = resolveCurrencyLocale(localeCode);

  return (
    [...codigos]
      .map(code => {
        const name = getCurrencyName(code, localeCode);
        return {
          code,
          name,
          // O código entra no rótulo porque nome de moeda se repete ("Dólar" é
          // americano, australiano, canadense) e porque é o código que a pessoa
          // reconhece ao conferir o que ficou salvo.
          label: name === code ? code : `${code} — ${name}`,
        };
      })
      // A moeda JÁ ESCOLHIDA nunca é filtrada, nem se for das mortas: o seletor
      // não pode trocar calado o que está gravado no campo só porque a moeda
      // saiu de circulação depois. Quem decide se troca é quem abriu a tela.
      .filter(
        opcao => opcao.code === escolhida || !SUPERSEDED_NAME.test(opcao.name)
      )
      .sort((a, b) => a.label.localeCompare(b.label, idioma))
  );
};

/**
 * Símbolo da moeda, como o locale de quem lê o escreve (`R$`, `US$`, `€`).
 *
 * Sai do `formatToParts`, e não de um mapa de símbolos escrito à mão, porque a
 * grafia muda com o locale: em `en` o real é `R$`, em `pt-BR` o dólar é `US$`.
 *
 * @param {string} currencyCode
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @returns {string}
 */
export const getCurrencySymbol = (currencyCode, localeCode) => {
  const currency = normalizeCurrencyCode(currencyCode);

  try {
    const partes = new Intl.NumberFormat(resolveCurrencyLocale(localeCode), {
      style: 'currency',
      currency,
    }).formatToParts(0);

    const simbolo = partes.find(parte => parte.type === 'currency');
    return simbolo ? simbolo.value : currency;
  } catch (error) {
    return currency;
  }
};

/**
 * Quantas casas decimais a moeda tem.
 *
 * Não é sempre dois: o iene e o peso chileno não têm subunidade (0), o dinar
 * kuwaitiano tem três. Quem sabe disso é o catálogo do `Intl`, e é ele quem
 * responde -- do contrário o campo recusaria `1000` em ienes pedindo centavos
 * que não existem.
 *
 * @param {string} currencyCode
 * @returns {number}
 */
export const getCurrencyDecimals = currencyCode => {
  const currency = normalizeCurrencyCode(currencyCode);

  try {
    const { maximumFractionDigits } = new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
    }).resolvedOptions();

    return Number.isInteger(maximumFractionDigits)
      ? maximumFractionDigits
      : DEFAULT_CURRENCY_DECIMALS;
  } catch (error) {
    return DEFAULT_CURRENCY_DECIMALS;
  }
};

/**
 * Converte para número, devolvendo `null` para o que não é numérico -- string
 * vazia inclusive, que o `Number('')` transformaria em zero.
 *
 * @param {string|number} value
 * @returns {number|null}
 */
const toNumber = value => {
  const texto = String(value === null || value === undefined ? '' : value)
    .trim()
    .replace(',', '.');
  if (!texto) return null;

  const numero = Number(texto);
  return Number.isFinite(numero) ? numero : null;
};

/**
 * Quantas casas decimais o valor DIGITADO tem.
 *
 * A conta é feita sobre a grafia, e não sobre o número, porque a pergunta é
 * sobre o que a pessoa escreveu. Zeros à direita não contam: "150,50" tem uma
 * casa significativa e é dinheiro válido de qualquer forma.
 *
 * @param {string|number} value
 * @returns {number}
 */
export const countCurrencyDecimals = value => {
  const texto = String(value === null || value === undefined ? '' : value)
    .trim()
    .replace(',', '.');
  if (!texto) return 0;

  const numero = toNumber(texto);
  if (numero === null) return 0;

  // Notação científica ('1e-3') não traz as casas na própria grafia; o toFixed
  // as revela. Doze é folga suficiente para dinheiro sem trazer ruído de ponto
  // flutuante.
  const plano = /e/i.test(texto) ? numero.toFixed(12) : texto;
  const decimais = (plano.split('.')[1] || '').replace(/0+$/, '');

  return decimais.length;
};

/**
 * O valor cabe na subunidade da moeda?
 *
 * Vazio passa: quem cobra valor obrigatório é o `required`, e somar as duas
 * mensagens no mesmo campo só confunde.
 *
 * @param {string|number} value
 * @param {string} currencyCode
 * @returns {boolean}
 */
export const hasValidCurrencyPrecision = (value, currencyCode) =>
  countCurrencyDecimals(value) <= getCurrencyDecimals(currencyCode);

/**
 * O número com os separadores do locale e as casas da moeda -- SEM o símbolo.
 *
 * Existe separado do `formatCurrency` porque o painel de detalhes desenha o
 * símbolo como um elemento próprio, fixo à esquerda da caixa (referência da
 * #1243). O `Intl` com `style: 'currency'` muda o símbolo de lado conforme o
 * locale (`R$ 1.234,50` em pt-BR, `1 234,50 €` em fr), o que quebraria esse
 * desenho.
 *
 * @param {string|number} value
 * @param {string} currencyCode
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @returns {string} Ex.: '1.234,50'
 */
export const formatCurrencyAmount = (value, currencyCode, localeCode) => {
  const numero = toNumber(value);
  if (numero === null)
    return String(value === null || value === undefined ? '' : value);

  const casas = getCurrencyDecimals(currencyCode);

  try {
    return new Intl.NumberFormat(resolveCurrencyLocale(localeCode), {
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    }).format(numero);
  } catch (error) {
    return numero.toFixed(casas);
  }
};

/**
 * Dinheiro completo, com símbolo no lugar que o locale usa.
 *
 * @param {string|number} value
 * @param {string} currencyCode
 * @param {string} localeCode - Código do locale da conta (ex.: pt_BR)
 * @returns {string} Ex.: 'R$ 1.234,50'
 */
export const formatCurrency = (value, currencyCode, localeCode) => {
  const numero = toNumber(value);
  if (numero === null)
    return String(value === null || value === undefined ? '' : value);

  const casas = getCurrencyDecimals(currencyCode);

  try {
    return new Intl.NumberFormat(resolveCurrencyLocale(localeCode), {
      style: 'currency',
      currency: normalizeCurrencyCode(currencyCode),
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    }).format(numero);
  } catch (error) {
    return `${getCurrencySymbol(currencyCode, localeCode)} ${numero.toFixed(
      casas
    )}`;
  }
};
